// 批次发货明细：套装一行按「套」，下面逐个组件列发货、破损/补耗、实际出库（单位「个」）；单品一行一个产品。
// 照订单详情 Batch 的做法；套数口径见 bundle.ts。
import { num, qty } from '@/lib/format';
import { modeOf, ruleText, type ShipLineGroup } from './bundle';
import type { ShipmentListItem } from './types';
import s from './ShipmentLines.module.css';

type LineNote = (line: { orderLineId?: string; productId: string }) => string;

function Remark({ text }: { text: string }) {
  return <td className={s.remark} title={text || undefined}>{text || '—'}</td>;
}

function LossCell({ v }: { v: number }) {
  return <td className={`${s.r} ${v ? 'neg' : ''}`}>{qty(v)}</td>;
}

export function ShipmentLines({ groups, noteForLine }: { groups: ShipLineGroup[]; noteForLine: LineNote }) {
  return (
    <table className={s.tbl} aria-label="发货明细">
      <thead>
        <tr>
          <th>产品</th>
          <th className={s.code}>产品编号</th>
          <th className={s.r}>发货数量</th>
          <th className={s.r}>破损 / 补耗</th>
          <th className={s.r}>实际出库</th>
          <th>备注</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((g) =>
          g.kind === 'bundle' ? (
            [
              <tr key={g.key} className={s.kit}>
                <td><span className={s.name}>{g.name}</span> <span className="muted">{g.lines.length} 个组件</span></td>
                <td className={`${s.code} em`}>{g.code || '—'}</td>
                <td className={s.r}>{g.sets === null ? '—' : `${qty(g.sets)} 套`}</td>
                <td className={`${s.r} ${g.loss ? 'neg' : ''}`}>{qty(g.loss)} 个</td>
                <td className={s.r}>{qty(g.ship + g.loss)} 个</td>
                <td className={s.remark}>—</td>
              </tr>,
              ...g.lines.map((l) => (
                <tr key={l.id} className={s.child}>
                  <td>
                    <span className={s.name}>{l.product.name}</span>
                    {l.orderLine && <span className={`${s.rule} ${modeOf(l.orderLine) === 'PER_GROUP' ? s.group : ''}`}>{ruleText(l.orderLine)}</span>}
                  </td>
                  <td className={`${s.code} em`}>{l.product.code}</td>
                  <td className={s.r}>{qty(l.quantity)}</td>
                  <LossCell v={num(l.lossQuantity)} />
                  <td className={s.r}>{qty(num(l.quantity) + num(l.lossQuantity))}</td>
                  <Remark text={noteForLine(l)} />
                </tr>
              )),
            ]
          ) : (
            <tr key={g.key}>
              <td><span className={s.name}>{g.line.product.name}</span></td>
              <td className={`${s.code} em`}>{g.line.product.code}</td>
              <td className={s.r}>{qty(g.line.quantity)}</td>
              <LossCell v={num(g.line.lossQuantity)} />
              <td className={s.r}>{qty(num(g.line.quantity) + num(g.line.lossQuantity))}</td>
              <Remark text={noteForLine(g.line)} />
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
}

/** 退路：订单详情取不到时只有台账数据（没有 orderLine），按产品逐行列出，不合并套装 */
export function FlatLines({ lines, noteForLine }: { lines: ShipmentListItem['lines']; noteForLine: LineNote }) {
  return (
    <table className={s.tbl} aria-label="发货明细">
      <thead>
        <tr>
          <th>产品</th>
          <th className={s.code}>产品编号</th>
          <th className={s.r}>发货数量</th>
          <th className={s.r}>破损 / 补耗</th>
          <th className={s.r}>实际出库</th>
          <th>备注</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id}>
            <td><span className={s.name}>{l.product.name}</span></td>
            <td className={`${s.code} em`}>{l.product.code}</td>
            <td className={s.r}>{qty(l.quantity)}</td>
            <LossCell v={num(l.lossQuantity)} />
            <td className={s.r}>{qty(num(l.quantity) + num(l.lossQuantity))}</td>
            <Remark text={noteForLine({ productId: l.product.id })} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}
